/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import {
    AuiFooter,
    AuiHeader,
    AuiSessionTimeout,
    AuiSidenav,
    AuiSupernav,
    SLoader,
    SToasts,
} from "@avalara/skylab-sdk/react";
import debounce from "lodash/debounce";
import React, {
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { getCertificateStatusesAsync } from "../../app/certificateSlice";
import { fetchRetailSettings } from "../../app/retailSettingsSlice";
import {
    getSessionAsync,
    refreshSessionContextAsync,
    selectSession,
    selectSkylabHeader,
} from "../../app/sessionSlice";
import type { SessionState } from "../../app/sessionSlice/types";
import { fetchSettingsAPI } from "../../app/settingsSlice";
import { AppDispatch } from "../../app/types";
import getConfig from "../../config";
import featureFlag from "../../featureToggler/featureFlag";
import "../../globalTypes";
import { useIsNoAccessUser } from "../../hooks/useAccessControl";
import { useIsAuthorized } from "../../hooks/useIsAuthorized";
import { useTokenProvider, type TokenProviderFunction } from "../../hooks/useTokenProvider";
import isOneWebDomainRuntime from "../../shared/avaAppRuntime";
import { avataxAPUrls, SessionRefreshState, SessionRefreshStateType } from "../../shared/constants";
import { clearLocalStorage, retainCriticalData, urlToPageMap } from "../../shared/Utils";
import zoominWidget from "../../shared/zoominWidget";
import ErrorBoundaryWrapper from "../errorHandling/errorBoundary";
import Navigation from "../navigation/Navigation";
import { NavigationVisibilityContext } from "../navigation/NavigationVisibilityProvider";
import { NavigationVisibilityContextType } from "../navigation/types";
import Provision from "../provision/Provision";
import { useSideNavFeatureFlag } from "../sharedComponents/useSideNavFeatureFlag";

enum navTabs {
    EXEMPTIONS = "aui-supernav.exemptions",
    SETTINGS = "aui-supernav.settings",
}

const { appEnv, hostnames } = getConfig();
const { ecmApiHost, identity } = hostnames;

const getMainContentClassName = (isRetailView: boolean, hideNav: boolean): string => {
    if (isRetailView || !hideNav) {
        return "dl-content flex dl-flex-fill-height";
    }
    return "flex dl-flex-fill-height";
};

// Helper function to determine app DOM content
const getAppDOMContent = (
    session: SessionState,
    isNoAccessUserFlag: boolean,
    currentLocation: string,
    isSideNavEnabled: boolean,
    isLoading: boolean,
    hasInitialSessionLoaded: boolean
): React.JSX.Element | null => {
    if (isLoading || !hasInitialSessionLoaded) {
        return (
            <div className="loader-container">
                <h1>Loading ...</h1>
                <SLoader id="page-loader" className="large" aria-live="polite" loading />
            </div>
        );
    }

    if (
        !isLoading &&
        ((session.accountId && (session.provisionStatus === "Provisioned" || isNoAccessUserFlag)) ||
            currentLocation.includes("health-check"))
    ) {
        return <Navigation isSideNavEnabled={isSideNavEnabled} />;
    }

    if (session.accountId) {
        return (
            <div className={`margin-top-lg provision-container`}>
                <Provision />
            </div>
        );
    }

    return null;
};

function Application(): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const session = useSelector(selectSession, shallowEqual) as SessionState;

    const header = useSelector(selectSkylabHeader, shallowEqual);

    const { hideNav }: NavigationVisibilityContextType = useContext(NavigationVisibilityContext);
    const isNoAccessUserFlag = useIsNoAccessUser();
    const currentLocation: string = window.location.href;
    const isRetailView =
        !currentLocation.includes(urlToPageMap.settings) &&
        currentLocation.includes(urlToPageMap.retail);
    const sideNavRef = useRef<React.ElementRef<typeof AuiSidenav>>(null);
    const superNavRef = useRef<React.ElementRef<typeof AuiSupernav>>(null);
    const [navActiveTab, setNavActiveTab] = useState<navTabs>(navTabs.EXEMPTIONS);

    const shouldSelectSettingsInSupernav = useIsAuthorized(
        featureFlag.topNav.activateSettingsTab,
        true
    );
    const errorBoundaryRef = useRef<{ removeError?: () => void }>();
    const zoomInWidgetId17 = useIsAuthorized(featureFlag.zoomInWidget.id17, true);
    const zoomInWidgetId18 = useIsAuthorized(featureFlag.zoomInWidget.id18, true);
    const zoomInWidgetId23 = useIsAuthorized(featureFlag.zoomInWidget.id23, true);
    const canAccessRetail = useIsAuthorized(featureFlag.retail.index, true);
    const isSideNavEnabled = useSideNavFeatureFlag();
    const [refreshState, setRefreshState] = useState<SessionRefreshStateType>(
        SessionRefreshState.Idle
    );
    const [hasInitialSessionLoaded, setHasInitialSessionLoaded] = useState(false);
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isLoading = session.isLoading || refreshState === SessionRefreshState.Refreshing;

    const logout = (): void => {
        retainCriticalData();
        clearLocalStorage();
        const baseURL: string =
            appEnv === "local" ? `http://${ecmApiHost}/api` : `https://${ecmApiHost}/api`;
        window.location.href = `${baseURL}/v3/auth/logout`;
    };

    useEffect(() => {
        if (!currentLocation.includes("/health-check")) dispatch(getSessionAsync());
        const documentHeader = document.querySelector("aui-header");
        if (documentHeader) {
            documentHeader.addEventListener("click", (event: Event) => {
                const target = event.target as HTMLElement;
                if (target && target.textContent === "Sign out") {
                    logout();
                }
            });
        }
    }, [dispatch, currentLocation]);

    useEffect(() => {
        if (!hasInitialSessionLoaded && session.isAuthenticated) {
            setHasInitialSessionLoaded(true);
        }
    }, [session.isAuthenticated, hasInitialSessionLoaded]);

    useEffect(() => {
        if (session.activeCompany.id && session.provisionStatus === "Provisioned") {
            dispatch(fetchSettingsAPI());
            dispatch(getCertificateStatusesAsync());
            if (canAccessRetail) {
                dispatch(fetchRetailSettings());
            }
        }
        if (session.activeCompany.name) {
            document.title = `Avatax | ${session.activeCompany.name}`;
        }
    }, [
        dispatch,
        session.activeCompany.id,
        session.activeCompany.name,
        session.provisionStatus,
        session.subscriptions,
        canAccessRetail,
    ]);

    const debouncedRefresh = useMemo(
        () =>
            debounce(async () => {
                if (refreshState === SessionRefreshState.Refreshing) {
                    return;
                }

                setRefreshState(SessionRefreshState.Refreshing);

                refreshTimeoutRef.current = setTimeout(() => {
                    setRefreshState(SessionRefreshState.Idle);
                }, 15000);

                try {
                    const result = (await dispatch(refreshSessionContextAsync())) as boolean;
                    if (result) {
                        // Use SPA navigation instead of hard reload to preserve state
                        navigate("/", { replace: true });
                        // Refresh session state after navigation
                        await dispatch(getSessionAsync(true));
                    }
                } finally {
                    if (refreshTimeoutRef.current) {
                        clearTimeout(refreshTimeoutRef.current);
                        refreshTimeoutRef.current = null;
                    }
                    setRefreshState(SessionRefreshState.Idle);
                }
            }, 300),
        [dispatch, navigate, refreshState]
    );

    useEffect(() => {
        document.addEventListener("s-change-company", debouncedRefresh);

        return () => {
            document.removeEventListener("s-change-company", debouncedRefresh);
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [debouncedRefresh]);

    const getCurrentNavRef = useCallback((): HTMLElement | null => {
        return isSideNavEnabled ? sideNavRef.current : superNavRef.current;
    }, [isSideNavEnabled]);

    const setActiveMenuTab = useCallback(
        (currentURL: string): void => {
            const currentNav = getCurrentNavRef();
            if (currentNav) {
                const isMatch: boolean = avataxAPUrls.some((url: string) =>
                    currentURL.includes(url)
                );
                if (shouldSelectSettingsInSupernav && isMatch) setNavActiveTab(navTabs.SETTINGS);
                else setNavActiveTab(navTabs.EXEMPTIONS);
            }
        },
        [shouldSelectSettingsInSupernav, getCurrentNavRef]
    );

    useEffect(() => {
        setActiveMenuTab(window.location.pathname);
        if (appEnv !== "local" && session.isAuthenticated) {
            zoominWidget({ zoomInWidgetId17, zoomInWidgetId18, zoomInWidgetId23 });
        }
    }, [
        session.isAuthenticated,
        setActiveMenuTab,
        zoomInWidgetId17,
        zoomInWidgetId18,
        zoomInWidgetId23,
    ]);

    const tokenProvider = useTokenProvider();

    useEffect(() => {
        if (!isOneWebDomainRuntime()) {
            const ipuaPanel = document.querySelector("ipua-panel") as HTMLElement & {
                getaccesstoken?: TokenProviderFunction;
            };
            if (ipuaPanel && session.accessToken !== null) {
                ipuaPanel.getaccesstoken = tokenProvider;
            }
        }
        dispatch(getSessionAsync());
    }, [dispatch, session.accessToken, tokenProvider]);

    const handleNavigation = useCallback(
        (nativeMouseEvent: MouseEvent, url: string): void => {
            const uri = new URL(url);
            if (uri.hostname.includes("exemptions")) {
                nativeMouseEvent.preventDefault();
                errorBoundaryRef.current?.removeError?.();
                navigate(`${uri.pathname}${uri.search}`);
                // (Only for top nav)The menu dropdown doesn't close automatically on selecting an item. So, have to programmatically click the expanded menu button to close it.
                if (!isSideNavEnabled) {
                    const currentNav = getCurrentNavRef();
                    if (currentNav) {
                        Array.from(currentNav.getElementsByTagName("button"))
                            ?.find(
                                (button: HTMLButtonElement) =>
                                    button.getAttribute("aria-expanded") === "true"
                            )
                            ?.click();
                    }
                }
            }
        },
        [navigate, isSideNavEnabled, getCurrentNavRef]
    );

    const navMemo = useMemo(
        () =>
            isSideNavEnabled ? (
                <AuiSidenav
                    active={navActiveTab}
                    requestCurrentAccessToken={tokenProvider}
                    ref={sideNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ) : (
                <AuiSupernav
                    active={navActiveTab}
                    requestCurrentAccessToken={tokenProvider}
                    ref={superNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ),
        [isSideNavEnabled, tokenProvider, navActiveTab, handleNavigation]
    );

    const auiSessionTimeoutMemo = useMemo(
        () => <AuiSessionTimeout onS-timeout={() => logout()} />,
        []
    );

    const appDOM: React.JSX.Element | null = getAppDOMContent(
        session,
        isNoAccessUserFlag,
        currentLocation,
        isSideNavEnabled,
        isLoading,
        hasInitialSessionLoaded
    );

    if (isOneWebDomainRuntime()) {
        return <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>;
    }

    return (
        <div
            className="app-container flex dl-flex-fill-height"
            data-testid={testIDs.common.container}>
            {session.accessToken ? (
                <AuiHeader
                    sideNavHeader={isSideNavEnabled}
                    appKey={header.appKey}
                    user={header.user}
                    logoutUrl={header.logoutUrl}
                    onS-navigate={e => retainCriticalData(e)}
                    integrateHelp={!isRetailView}
                    requestCurrentAccessToken={tokenProvider}
                    className={
                        isRetailView
                            ? "hide-account-products-usage hide-account-support-cases hide-account-billing hide-account-change-password hide-account-id hide-account-my-downloads"
                            : ""
                    }
                    showChatButton={!isRetailView}
                    data-testid={testIDs.shared.header}
                />
            ) : null}
            <div className={`app-content ${isSideNavEnabled ? "" : "flex dl-flex-fill-height"}`}>
                {session.accessToken && !hideNav ? navMemo : null}
                <div
                    className={`main-content margin-top-lg ${
                        isSideNavEnabled
                            ? "dl-content-sidenav"
                            : getMainContentClassName(isRetailView, hideNav)
                    }`}>
                    <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>
                </div>
            </div>
            <SToasts role="status" />
            {!isSideNavEnabled ? <AuiFooter user={header.user} /> : null}
            {session.accessToken !== null ? <ipua-panel product="ECM" url={identity} /> : null}
            {auiSessionTimeoutMemo}
        </div>
    );
}

export default React.memo(Application);
